import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { GoogleLogin } from "@react-oauth/google";
import { useFormContext } from "react-hook-form";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";

import { useAuth } from "../../context/Auth";

const DefaultLogin: React.FC<LoginProps> = ({ onSubmit, t }) => {
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const {
    register,
    formState: { errors },
  } = useFormContext();

  const { loginWithGoogle } = useAuth();

  const handleGoogleLoginSuccess = async (credentialResponse: any) => {
    try {
      const token = credentialResponse.credential;
      await loginWithGoogle(token);
    } catch (err: any) {
      const message =
        err?.message ||
        "An unexpected error occurred during Google login. Please try again.";
      toast.error(message);
    }
  };

  const handleGoogleLoginError = () => {
    toast.error<string>(
      "Google login failed. Please check your internet connection or try again.",
    );
  };

  const triggerGoogleLogin = () => {
    const googleButton = googleButtonRef.current?.querySelector(
      'div[role="button"]',
    ) as HTMLElement;
    if (googleButton) {
      googleButton.click();
    }
  };

  return (
    <div
      className="vh-100 d-flex align-items-center"
      style={{ background: "white" }}
    >
      <Container>
        <Row className="justify-content-center">
          <Col md={6}>
            <Form onSubmit={onSubmit}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="login-email">
                  {t("login:form.email.label")}
                </Form.Label>
                <Form.Control
                  type="email"
                  id="login-email"
                  {...register("email")}
                  placeholder={t("login:form.email.placeholder")}
                  autoComplete="username"
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {typeof errors.email?.message === "string"
                    ? errors.email.message
                    : null}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label htmlFor="login-password">
                  {t("login:form.password.label")}
                </Form.Label>
                <Form.Control
                  type="password"
                  id="login-password"
                  {...register("password")}
                  placeholder={t("login:form.password.placeholder")}
                  autoComplete="current-password"
                  isInvalid={!!errors.password}
                />
                <Form.Control.Feedback type="invalid">
                  {typeof errors.password?.message === "string"
                    ? errors.password.message
                    : null}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="d-flex justify-content-between">
                <Button variant="primary" type="submit">
                  {t("login:button.submit")}
                </Button>
                <Button variant="secondary" as={Link} to="/forgot-password">
                  {t("login:button.forgotPassword")}
                </Button>
              </div>
            </Form>
            <div className="auth-separator my-4">
              <span>OR</span>
            </div>
            <div className="text-center">
              <Button
                variant="outline-primary"
                onClick={triggerGoogleLogin}
                className="w-100 d-flex align-items-center justify-content-center gap-2"
                style={{ padding: "0.75rem 2rem" }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <p className="mb-0 ml-2">Sign in with Google</p>
              </Button>

              <div ref={googleButtonRef} style={{ display: "none" }}>
                <GoogleLogin
                  onSuccess={handleGoogleLoginSuccess}
                  onError={handleGoogleLoginError}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DefaultLogin;
